import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, TrendingUp, Users, Lock, Clock, MapPin, Award, Heart, Eye, Handshake } from 'lucide-react';
export function About() {
  const coreValues = [{
    icon: Award,
    title: 'Integrity',
    description: 'Honest and transparent operations in every transaction and process.'
  }, {
    icon: Users,
    title: 'Professionalism',
    description: 'Skilled smelters delivering quality service with expertise and care.'
  }, {
    icon: Lock,
    title: 'Confidentiality',
    description: 'Secure handling of client materials with complete discretion.'
  }, {
    icon: Handshake,
    title: 'Reliability',
    description: 'Timely and dependable service you can count on every time.'
  }];
  const features = [{
    icon: Users,
    title: 'Experienced Smelters',
    description: 'Highly trained professionals with years of expertise in gold purification.'
  }, {
    icon: TrendingUp,
    title: 'Modern Equipment',
    description: 'State-of-the-art smelting technology ensuring highest accuracy and purity.'
  }, {
    icon: MapPin,
    title: 'On-Site & Mobile',
    description: 'Flexible service options at our Bagamoyo Road facility or your location.'
  }, {
    icon: Lock,
    title: 'Secure Process',
    description: 'Complete transparency and security throughout the smelting process.'
  }, {
    icon: ShieldCheck,
    title: 'Reliable Support',
    description: 'Dedicated customer service for miners, dealers, and individuals.'
  }, {
    icon: Clock,
    title: 'Fast Turnaround',
    description: 'Efficient processing without compromising on quality or precision.'
  }];
  return <section id="about" className="py-24 bg-black-DEFAULT relative overflow-hidden" data-id="element-1">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-2">
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gold-900/10 rounded-full blur-[120px]" data-id="element-3" />
        <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gold-800/5 rounded-full blur-[100px]" data-id="element-4" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-5">
        <div className="text-center mb-16" data-id="element-6">
          <motion.span initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-gold-500 font-medium tracking-widest uppercase text-sm" data-id="element-7">
            About Us
          </motion.span>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="mt-3 text-3xl md:text-5xl font-bold text-white" data-id="element-8">
            Kings Gold Smelters Company Limited
          </motion.h2>
          <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="mt-4 h-1 w-24 bg-gold-500 mx-auto rounded-full" data-id="element-9" />
        </div>

        {/* About Us Story */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-4xl mx-auto mb-20" data-id="element-10">
          <div className="bg-black-light border border-gray-800 rounded-lg p-8 md:p-12" data-id="element-11">
            <p className="text-gray-300 text-lg leading-relaxed mb-6" data-id="element-12">
              Kings Gold Smelters Company Limited is a professional gold
              smelting company based in Dar es Salaam, Tanzania, located at
              Bagamoyo Road. We specialize in providing reliable, secure, and
              accurate gold smelting services to miners, gold dealers, and
              individual clients across Tanzania.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mb-6" data-id="element-13">
              Founded with a commitment to professionalism and integrity, our
              company operates with experienced smelters and modern equipment to
              ensure high-quality results. We understand the value of trust in
              the gold industry, which is why we emphasize transparency,
              confidentiality, and precision in every smelting process.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed mb-6" data-id="element-14">
              We offer both on-site gold smelting services at our company
              premises and mobile gold smelting services, where our professional
              team travels to client locations to perform smelting safely and
              efficiently. This flexibility allows us to meet the diverse needs
              of our clients, whether at mining sites, private premises, or
              business locations.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed" data-id="element-15">
              At Kings Gold Smelters Company Limited, our focus is on delivering
              dependable services while maintaining strict professional
              standards. We are dedicated to building long-term relationships
              with our clients by offering consistent quality, honest service,
              and customer satisfaction.
            </p>
          </div>
        </motion.div>

        {/* Core Values */}
        <div className="mb-20" data-id="element-16">
          <div className="text-center mb-12" data-id="element-17">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-18">
              Our Core Values
            </h3>
            <p className="text-gray-400 max-w-2xl mx-auto" data-id="element-19">
              The principles that guide every decision and action we take
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-20">
            {coreValues.map((value, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-black-rich border border-gray-800 p-8 rounded-lg text-center hover:border-gold-500/50 transition-all duration-300 group" data-id="element-21">
                <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-gold-500/20 transition-colors" data-id="element-22">
                  <value.icon className="w-8 h-8 text-gold-500" data-id="element-23" />
                </div>
                <h4 className="text-xl font-bold text-white mb-3" data-id="element-24">
                  {value.title}
                </h4>
                <p className="text-gray-400 text-sm leading-relaxed" data-id="element-25">
                  {value.description}
                </p>
              </motion.div>)}
          </div>
        </div>

        {/* Mission & Vision with Real Smelting Image */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20" data-id="element-26">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="space-y-8" data-id="element-27">
            <div className="bg-black-light border border-gray-800 p-8 rounded-lg relative overflow-hidden group hover:border-gold-500/30 transition-colors" data-id="element-28">
              <div className="absolute top-0 left-0 w-1 h-full bg-gold-500" data-id="element-29" />
              <h3 className="text-2xl font-bold text-white mb-4" data-id="element-30">
                Our Mission
              </h3>
              <p className="text-gray-400 leading-relaxed" data-id="element-31">
                To provide professional, safe, and efficient gold smelting
                services while maintaining the highest standards of accuracy,
                integrity, and customer trust.
              </p>
            </div>

            <div className="bg-black-light border border-gray-800 p-8 rounded-lg relative overflow-hidden group hover:border-gold-500/30 transition-colors" data-id="element-32">
              <div className="absolute top-0 left-0 w-1 h-full bg-gold-500" data-id="element-33" />
              <h3 className="text-2xl font-bold text-white mb-4" data-id="element-34">Our Vision</h3>
              <p className="text-gray-400 leading-relaxed" data-id="element-35">
                To become one of the leading gold smelting service providers in
                Tanzania by offering reliable on-site and mobile smelting
                solutions that meet international standards.
              </p>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="relative" data-id="element-36">
            <div className="aspect-[4/3] rounded-lg overflow-hidden border border-gray-800" data-id="element-37">
              <img src="https://cdn.magicpatterns.com/uploads/h58s8oZznDqzvSeMURChQs/1593219785603.jpg" alt="Professional smelter working with gold bars" className="w-full h-full object-cover opacity-90 hover:opacity-100 transition-opacity duration-700 hover:scale-105 transform" data-id="element-38" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60" data-id="element-39" />
              <div className="absolute bottom-6 left-6 right-6" data-id="element-40">
                <p className="text-white font-bold text-xl" data-id="element-41">
                  Professional Excellence
                </p>
                <p className="text-gold-400 text-sm mt-1" data-id="element-42">
                  Expert smelters at work in our facility
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="text-center mb-12" data-id="element-43">
          <h3 className="text-2xl font-bold text-white mb-4" data-id="element-44">Why Choose Us</h3>
          <p className="text-gray-400 max-w-2xl mx-auto" data-id="element-45">
            We combine traditional expertise with modern technology to deliver
            superior results.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-46">
          {features.map((feature, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className="bg-black-rich border border-gray-800 p-6 rounded-lg hover:border-gold-500/50 transition-all duration-300 group" data-id="element-47">
              <div className="w-12 h-12 bg-gray-900 rounded-full flex items-center justify-center mb-4 group-hover:bg-gold-500/20 transition-colors" data-id="element-48">
                <feature.icon className="w-6 h-6 text-gold-500" data-id="element-49" />
              </div>
              <h4 className="text-xl font-bold text-white mb-2" data-id="element-50">
                {feature.title}
              </h4>
              <p className="text-gray-400 text-sm leading-relaxed" data-id="element-51">
                {feature.description}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}